import throttle from 'lodash-es/throttle';
import { defineModule } from '@/js/utils/helpers';

const IFRAME_MIN_HEIGHT = 100; // Minimum height for the iframe
const IFRAME_HEIGHT_THRESHOLD = 3; // Ignore minor changes
const IFRAME_OFFSET_MARGIN = 5;
const IFRAME_THROTTLE_DELAY = 300; // Delay in ms

const getElements = () => ({
  statementIframes: document.querySelectorAll<HTMLIFrameElement>(
    '.content-block--statements iframe',
  ),
});

const previousHeights = new WeakMap<HTMLIFrameElement, number>();

const updateIframeHeight = throttle(
  (iframe: HTMLIFrameElement, newHeight: number) => {
    const prevHeight = previousHeights.get(iframe) ?? 0;

    if (Math.abs(newHeight - prevHeight) <= IFRAME_HEIGHT_THRESHOLD) return;

    iframe.style.height = `${newHeight + IFRAME_OFFSET_MARGIN}px`;
    previousHeights.set(iframe, newHeight);
  },
  IFRAME_THROTTLE_DELAY,
  { leading: true, trailing: true }, // delay in ms
);

const onIframeHeightMessage = (
  event: MessageEvent,
  iframes: HTMLIFrameElement[],
) => {
  if (event.data.type !== 'iframeHeight') return;

  const { height } = event.data;
  if (height < IFRAME_MIN_HEIGHT) return;

  iframes.forEach((iframe) => {
    updateIframeHeight(iframe, height);
  });
};

const onIframeSetMunicipalityMessage = async (event: MessageEvent) => {
  if (event.data.type !== 'setMunicipality') return;

  const { municipality } = event.data;
  if (!municipality) return;

  try {
    await fetch('/wp-json/polis/v1/vote', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ municipality }),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to store municipality vote:', err);
  }
};

const onIframeMessage = (event: MessageEvent) => {
  if (!['iframeHeight', 'setMunicipality'].includes(event.data.type)) return;

  const { statementIframes } = getElements();
  const matchingIframes = Array.from(statementIframes).filter((iframe) =>
    iframe.src.startsWith(event.origin),
  );
  if (!matchingIframes.length) return;

  switch (event.data.type) {
    case 'iframeHeight':
      onIframeHeightMessage(event, matchingIframes);
      break;
    case 'setMunicipality':
      onIframeSetMunicipalityMessage(event);
      break;
    default:
      // unsupported data types
      break;
  }
};

export default defineModule(
  () => {
    const { statementIframes } = getElements();
    if (!statementIframes.length) return;

    window.addEventListener('message', onIframeMessage);
  },
  () => {
    const { statementIframes } = getElements();
    if (!statementIframes.length) return;

    window.removeEventListener('message', onIframeMessage);
  },
);
